import React from 'react';
import {
  UsersIcon,
  UserGroupIcon,
  SparklesIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

interface KpiMetrics {
  totalContacts: number;
  activeClients: number;
  newLeads: number;
  avgResponseTime: number;
}

interface KpiSummaryBarProps {
  metrics: KpiMetrics;
}

const KpiSummaryBar: React.FC<KpiSummaryBarProps> = ({ metrics }) => {
  const kpiCards = [
    {
      title: 'Total Contacts',
      value: metrics.totalContacts,
      Icon: UsersIcon,
      iconColor: 'text-lex-accent',
      valueColor: 'text-lex-accent',
      bgGradient: 'bg-lex-accent-soft',
      borderColor: 'border-lex-accent/30',
      glowColor: 'hover:shadow-lex-accent/20',
    },
    {
      title: 'Active Clients',
      value: metrics.activeClients,
      Icon: UserGroupIcon,
      iconColor: 'text-emerald-500 dark:text-emerald-400',
      valueColor: 'text-emerald-600 dark:text-emerald-400',
      bgGradient: 'bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-900/30 dark:to-emerald-800/20',
      borderColor: 'border-emerald-200/50 dark:border-emerald-700/30',
      glowColor: 'hover:shadow-emerald-500/20',
      pulse: true, // Active clients get subtle pulse
    },
    {
      title: 'New Leads (30d)',
      value: metrics.newLeads,
      Icon: SparklesIcon,
      iconColor: 'text-amber-500 dark:text-amber-400',
      valueColor: 'text-amber-600 dark:text-amber-400',
      bgGradient: 'bg-gradient-to-br from-amber-50 to-amber-100/50 dark:from-amber-900/30 dark:to-amber-800/20',
      borderColor: 'border-amber-200/50 dark:border-amber-700/30',
      glowColor: 'hover:shadow-amber-500/20',
    },
    {
      title: 'Avg. Response Time',
      value: `${metrics.avgResponseTime}h`,
      Icon: ClockIcon,
      iconColor: 'text-violet-500 dark:text-violet-400',
      valueColor: 'text-violet-600 dark:text-violet-400',
      bgGradient: 'bg-gradient-to-br from-violet-50 to-violet-100/50 dark:from-violet-900/30 dark:to-violet-800/20',
      borderColor: 'border-violet-200/50 dark:border-violet-700/30',
      glowColor: 'hover:shadow-violet-500/20',
    },
  ];

  return (
    <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {kpiCards.map((card, index) => (
          <div
            key={index}
            className={`
              ${card.bgGradient}
              ${card.borderColor}
              ${card.glowColor}
              rounded-xl p-4
              border
              transition-all duration-300 ease-out
              hover:scale-[1.02] hover:shadow-lg
              cursor-default
              group
            `}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
                  {card.title}
                </p>
                <p className={`text-3xl font-bold ${card.valueColor} tabular-nums`}>
                  {typeof card.value === 'number' ? card.value.toLocaleString() : card.value}
                </p>
              </div>
              <div className={`
                ${card.iconColor}
                p-2.5 rounded-lg
                bg-white/60 dark:bg-gray-800/60
                backdrop-blur-sm
                transition-transform duration-300
                group-hover:scale-110 group-hover:rotate-3
                ${card.pulse ? 'animate-pulse' : ''}
              `}>
                <card.Icon className="h-7 w-7" strokeWidth={1.5} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default KpiSummaryBar; 